import {type ChangeEvent, useEffect, useMemo, useState} from "react";
import {Check, Clipboard, Download} from "lucide-react";
import {api} from "./api";
import {Button, Card, InlineNotice} from "./components/ui";
import {useI18n} from "./i18n";
import type {Manager} from "./types";

type Draft = {
  functionName: string;
  description: string;
  specName: string;
  specVersion: string;
  specDefinition: string;
  managerID: string;
  workerID: string;
  workerPath: string;
  runtime: string;
  requirements: string;
  controllerName: string;
  controllerRequirements: string;
  acceptance: string;
};

const emptyDraft: Draft = {
  functionName: "",
  description: "",
  specName: "",
  specVersion: "1.0",
  specDefinition: "{\n  \"input\": {},\n  \"result\": {}\n}",
  managerID: "",
  workerID: "wrk_",
  workerPath: "/opt/Emanager/workers/",
  runtime: "",
  requirements: "",
  controllerName: "",
  controllerRequirements: "",
  acceptance: "",
};

export function buildDevelopmentPrompt(draft: Draft, managerName = "") {
  return `# Layla TAB new function development task

You must follow the existing Platform public protocols and SDK skeletons. This is a constrained implementation task, not a new architecture design task. Business-specific logic must remain in the Worker and Controller; never add it to Platform Core or EManager Core.

## Fixed project information

- Function name: ${draft.functionName || "[required]"}
- Function description: ${draft.description || "[required]"}
- Manager: ${managerName || "[select Manager]"} (${draft.managerID || "[required]"})
- Worker ID: ${draft.workerID || "[required]"}
- Worker deployment path: ${draft.workerPath || "[required]"}
- Worker runtime/dependencies: ${draft.runtime || "[describe]"}
- Spec: ${draft.specName || "[required]"} ${draft.specVersion || "[required]"}
- Controller name: ${draft.controllerName || draft.functionName || "[required]"}

## Immutable Spec definition

\`\`\`json
${draft.specDefinition || "{}"}
\`\`\`

## Worker business requirements

${draft.requirements || "[required]"}

## Controller UI requirements

${draft.controllerRequirements || "[required]"}

## Acceptance requirements

${draft.acceptance || "Complete one real end-to-end task and verify progress, cancellation, S3 input/output Artifacts, reconnect, and failure reporting."}

## Mandatory development sequence

1. Validate the requirements and finalize the immutable Spec before runtime code.
2. Reuse the repository EWorker skeleton and EManager SDK. The Worker connects only to its local EManager; it must not access Platform credentials, database, Manager Key, or S3 credentials.
3. Implement the Worker exactly against the Spec. Read only declared input paths, write outputs only below the supplied output directory, and report started/progress/succeeded/failed through the SDK.
4. Register using the given Worker ID and selected Manager, then test Spec digest compatibility, heartbeat/reconnect, invalid input, cancellation, dependency failure, and a real task.
5. Reuse the Controller UI skeleton. Package a sandboxed static UI with controller.json and use only Platform Controller Host SDK operations for Specs, compatible Workers, uploads, tasks, progress, Artifacts, preview, and downloads.
6. The Controller must handle zero compatible Workers as a normal disabled empty state. It must never contact EManager, Worker, S3, or internal Platform APIs directly.
7. Run the complete Controller → Platform → EManager → Worker → S3 result loop and record commands, test evidence, deployed paths, and remaining limitations.

Do not change public protocols or shared Core behavior unless an actual general protocol defect is proven. If blocked, stop and report the exact completed stage and evidence.`;
}

export function DevelopmentPromptBuilder() {
  const {t} = useI18n();
  const [draft, setDraft] = useState<Draft>(() => {
    try {
      const saved = localStorage.getItem("layla-development-prompt-draft");
      return saved ? {...emptyDraft, ...JSON.parse(saved)} : emptyDraft;
    } catch {
      return emptyDraft;
    }
  });
  const [managers, setManagers] = useState<Manager[]>([]);
  const [copied, setCopied] = useState(false);
  const [jsonError, setJSONError] = useState("");

  useEffect(() => {
    api<{managers: Manager[] | null}>("/managers").then((result) => setManagers(Array.isArray(result.managers) ? result.managers : [])).catch(() => setManagers([]));
  }, []);

  useEffect(() => {
    localStorage.setItem("layla-development-prompt-draft", JSON.stringify(draft));
  }, [draft]);

  const selectedManager = managers.find((manager) => manager.id === draft.managerID);
  const prompt = useMemo(() => buildDevelopmentPrompt(draft, selectedManager?.name), [draft, selectedManager?.name]);

  function update(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const {name, value} = event.currentTarget;
    setDraft((current) => ({...current, [name]: value}));
    if (name === "specDefinition") {
      try { JSON.parse(value); setJSONError(""); } catch { setJSONError(t("Spec definition must be valid JSON.")); }
    }
  }

  async function copy() {
    await navigator.clipboard.writeText(prompt);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  function download() {
    const url = URL.createObjectURL(new Blob([prompt], {type: "text/markdown;charset=utf-8"}));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${draft.functionName.trim().replace(/[^a-zA-Z0-9_-]+/g, "-") || "layla-new-function"}-agent-task.md`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return <Card className="development-builder" title={t("New function Agent task generator")} description={t("Fill the variable business fields; Platform generates the fixed Worker and Controller development workflow.")}>
    <div className="generator-grid">
      <label className="field"><span>{t("Function name")}<b>*</b></span><input name="functionName" value={draft.functionName} onChange={update} required /></label>
      <label className="field"><span>{t("Controller name")}</span><input name="controllerName" value={draft.controllerName} onChange={update} placeholder={draft.functionName || "Controller UI"} /></label>
      <label className="field generator-wide"><span>{t("Function description")}<b>*</b></span><textarea name="description" value={draft.description} onChange={update} /></label>
      <label className="field"><span>{t("Manager")}<b>*</b></span><select name="managerID" value={draft.managerID} onChange={update}><option value="">{t("Select Manager")}</option>{managers.map((manager) => <option key={manager.id} value={manager.id}>{manager.name} · {manager.id}</option>)}</select></label>
      <label className="field"><span>{t("Worker ID")}<b>*</b></span><input name="workerID" value={draft.workerID} onChange={update} /></label>
      <label className="field"><span>{t("Spec name")}<b>*</b></span><input name="specName" value={draft.specName} onChange={update} /></label>
      <label className="field"><span>{t("Spec version")}<b>*</b></span><input name="specVersion" value={draft.specVersion} onChange={update} /></label>
      <label className="field"><span>{t("Worker path")}<b>*</b></span><input name="workerPath" value={draft.workerPath} onChange={update} /></label>
      <label className="field"><span>{t("Runtime and dependencies")}</span><input name="runtime" value={draft.runtime} onChange={update} /></label>
      <label className="field generator-wide"><span>{t("Immutable Spec JSON")}<b>*</b></span><textarea className="generator-spec" name="specDefinition" value={draft.specDefinition} onChange={update} spellCheck={false} /></label>
      {jsonError && <div className="generator-wide"><InlineNotice tone="error">{jsonError}</InlineNotice></div>}
      <label className="field generator-wide"><span>{t("Worker business requirements")}<b>*</b></span><textarea name="requirements" value={draft.requirements} onChange={update} /></label>
      <label className="field generator-wide"><span>{t("Controller UI requirements")}<b>*</b></span><textarea name="controllerRequirements" value={draft.controllerRequirements} onChange={update} /></label>
      <label className="field generator-wide"><span>{t("Acceptance additions")}</span><textarea name="acceptance" value={draft.acceptance} onChange={update} /></label>
    </div>
    <div className="agent-prompt-section"><div className="section-heading"><h3>{t("Generated Agent task")}</h3><p>{t("The draft is saved in this browser. Copy or download it after completing the required fields.")}</p></div><textarea className="agent-prompt generated-prompt" readOnly value={prompt} /><div className="form-actions"><Button onClick={() => void copy()} disabled={Boolean(jsonError)}>{copied ? <Check /> : <Clipboard />}{t(copied ? "Copied" : "Copy generated task")}</Button><Button variant="primary" onClick={download} disabled={Boolean(jsonError)}><Download />{t("Download Markdown")}</Button></div></div>
  </Card>;
}
